/**
 * Перенос `presentation/providers/person_questions_provider.dart`.
 *
 * `AutoDisposeFutureProviderFamily<List<PersonQuestionModel>, int>` —
 * семейство по `personId`; в вебе это [useAsyncValue] с тем же ключом.
 * Сетка подсказок читает результат через `matchAsyncValue`, как читала
 * `questions.when(loading: …, error: …, data: …)`.
 */

import { personRepository } from '@/data/personRepositoryImpl';
import type { PersonQuestionModel } from '@/domain/models/personQuestionModel';
import { type AsyncValue, useAsyncValue } from '@/presentation/stores/asyncValue';
import { DiagnosticsLog } from '@/utils/diagnostics/diagnosticsLog';
import { stackTraceOf } from '@/utils/exceptions/appException';
import { AppFailure, FailureOp } from '@/utils/exceptions/appFailure';

async function fetchQuestions(
  personId: number,
): Promise<readonly PersonQuestionModel[]> {
  try {
    return await personRepository.fetchQuestions(personId);
  } catch (error) {
    // Подсказки второстепенны: посетителю ничего не показываем,
    // но код сбоя уходит в лог, чтобы инженер его увидел.
    const stackTrace = stackTraceOf(error);
    const failure = AppFailure.fromException(FailureOp.questions, error, stackTrace);
    // `dev.log(failure.diagnostics, name: 'Failure', error: e, stackTrace: stk)`
    console.debug(`[Failure] ${failure.diagnostics}`, error, stackTrace);
    DiagnosticsLog.instance.failure(failure);
    // `rethrow` — состояние провайдера обязано стать `AsyncError`,
    // иначе сетка покажет пустой список вместо ничего.
    throw error;
  }
}

/** `ref.watch(personQuestionsProvider(personId))` */
export function usePersonQuestions(
  personId: number,
): AsyncValue<readonly PersonQuestionModel[]> {
  return useAsyncValue(() => fetchQuestions(personId), [personId]);
}
